import { z } from "zod";

/**
 * UUID v7 — app-side generation (per CLAUDE.md). The version nibble is the
 * 13th hex char; v7 is "7xxx". Lowercase only — having both case variants
 * represent the same id would break dedup/equality and DB lookups.
 */
const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const uuidV7 = () => z.string().regex(UUID_V7, "expected a UUID v7 (lowercase hex, version 7)");

/** Branded UUID v7 identifying an account (root tenant). */
export const AccountId = uuidV7().brand<"AccountId">();
export type AccountId = z.output<typeof AccountId>;

/** Branded UUID v7 identifying a master wallet (one per account in MVP). */
export const WalletId = uuidV7().brand<"WalletId">();
export type WalletId = z.output<typeof WalletId>;

/** Branded UUID v7 identifying a sub-wallet (child of a master wallet). */
export const SubWalletId = uuidV7().brand<"SubWalletId">();
export type SubWalletId = z.output<typeof SubWalletId>;

/** Branded UUID v7 identifying a transaction (the "what happened" header). */
export const TransactionId = uuidV7().brand<"TransactionId">();
export type TransactionId = z.output<typeof TransactionId>;

/** Branded UUID v7 identifying a single ledger entry (one row of a posting). */
export const LedgerEntryId = uuidV7().brand<"LedgerEntryId">();
export type LedgerEntryId = z.output<typeof LedgerEntryId>;

/** Branded UUID v7 identifying a master-approval request for an above-threshold spend. */
export const ApprovalId = uuidV7().brand<"ApprovalId">();
export type ApprovalId = z.output<typeof ApprovalId>;

/** Branded UUID v7 identifying an auth session (one per active login). */
export const SessionId = uuidV7().brand<"SessionId">();
export type SessionId = z.output<typeof SessionId>;

/** Branded UUID v7 identifying a programmatic API key (one per issued credential). */
export const ApiKeyId = uuidV7().brand<"ApiKeyId">();
export type ApiKeyId = z.output<typeof ApiKeyId>;

/** Branded UUID v7 identifying a registered outbound webhook endpoint. */
export const WebhookEndpointId = uuidV7().brand<"WebhookEndpointId">();
export type WebhookEndpointId = z.output<typeof WebhookEndpointId>;
